import type { MkLocationSuggestion } from '@seek/ca-graphql-schema/types';
import type { Dispatch, SetStateAction } from 'react';
import type { SearchCompanyProfileResult } from '../../../shared/services/companyProfileService/types';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../../shared/services/roleService/roleService';
import { CareerDevelopmentPage } from '../../pages/ReviewsExperimentTwoPages/CareerDevelopmentPage';
import { DiversityEqualOpptyPage } from '../../pages/ReviewsExperimentTwoPages/DiversityEqualOpptyPage';
import { ManagementPage } from '../../pages/ReviewsExperimentTwoPages/ManagementPage';
import { PerksAndBenefitsPage } from '../../pages/ReviewsExperimentTwoPages/PerksAndBenefitsPage';
import { ReviewCompanyPage } from '../../pages/ReviewsExperimentTwoPages/ReviewCompanyPage';
import { SalaryPage } from '../../pages/ReviewsExperimentTwoPages/SalaryPage';
import { SubmitSuccessPage as ExpTwoSubmitSuccessPage } from '../../pages/ReviewsExperimentTwoPages/SubmitSuccessPage';
import { WorkingEnvironmentPage } from '../../pages/ReviewsExperimentTwoPages/WorkingEnvironmentPage';
import { WorkLifeBalancePage } from '../../pages/ReviewsExperimentTwoPages/WorkLifeBalancePage';
import { YourRolePage } from '../../pages/ReviewsExperimentTwoPages/YourRolePage';
import {
  ExperimentTwoProgress,
  type ReviewFormContext,
  type ReviewPayload,
} from '../../type';

type ReviewStepRendererProps = {
  companySelectionIsValidated: boolean;
  getRoleTitles: (query: string) => Promise<RoleTitle[]>;
  handleOnSetPage: (page: ExperimentTwoProgress) => void;
  handleOnSubmit: () => Promise<void>;
  isSubmitting: boolean;
  onSearchCompanyProfiles: (
    query: string,
  ) => Promise<SearchCompanyProfileResult>;
  onSearchLocations: (
    query: string,
  ) => Promise<Partial<MkLocationSuggestion>[]>;
  onUpdateRole: (role: ConfirmedRoles) => Promise<void>;
  page: ExperimentTwoProgress;
  review: ReviewPayload;
  reviewFormContext: ReviewFormContext;
  setReview: Dispatch<SetStateAction<ReviewPayload>>;
};

export const reviewStepRenderer = ({
  page,
  review,
  reviewFormContext,
  onSearchCompanyProfiles,
  onSearchLocations,
  companySelectionIsValidated,
  handleOnSetPage,
  setReview,
  handleOnSubmit,
  isSubmitting,
  getRoleTitles,
  onUpdateRole,
}: ReviewStepRendererProps) => {
  const commonProps = {
    review,
    reviewFormContext,
    setPage: handleOnSetPage,
    setter: setReview,
    onSubmit: () => void handleOnSubmit(),
    isSubmitting,
    pageNumber: page,
  };

  switch (page) {
    case ExperimentTwoProgress.OverallRating:
      return (
        <ReviewCompanyPage
          {...commonProps}
          searchCompanyProfiles={onSearchCompanyProfiles}
          hideCompanyNameSearch={companySelectionIsValidated}
        />
      );
    case ExperimentTwoProgress.YourRole:
      return (
        <YourRolePage
          {...commonProps}
          getRoleTitles={getRoleTitles}
          onUpdateRole={onUpdateRole}
          onSearchLocations={onSearchLocations}
        />
      );

    case ExperimentTwoProgress.WorkLifeBalance:
      return <WorkLifeBalancePage {...commonProps} />;

    case ExperimentTwoProgress.PerksAndBenefits:
      return <PerksAndBenefitsPage {...commonProps} />;

    case ExperimentTwoProgress.Salary:
      return <SalaryPage {...commonProps} />;

    case ExperimentTwoProgress.CareerDevelopment:
      return <CareerDevelopmentPage {...commonProps} />;

    case ExperimentTwoProgress.Management:
      return <ManagementPage {...commonProps} />;

    case ExperimentTwoProgress.DiversityAndEqualOpportunity:
      return <DiversityEqualOpptyPage {...commonProps} />;

    case ExperimentTwoProgress.WorkingEnvironment:
      return <WorkingEnvironmentPage {...commonProps} />;

    case ExperimentTwoProgress.SubmitSuccess:
      return (
        <ExpTwoSubmitSuccessPage
          companyName={review.companyName ?? ''}
          reviewFormContext={reviewFormContext}
        />
      );

    default:
      return null;
  }
};
